<template>
  <div data-component-group="package-form" data-component="package-labels">
    <slot name="beforeForm" />

    <div v-if="!isDisabled" class="component-header">
      <magic-container>
        <v-autocomplete
          v-model="country"
          :items="countries"
          prepend-inner-icon="public"
          label="Country"
          item-title="name"
          item-value="code"
          :return-object="true"
          required
          :readonly="isDisabled"
          :error-messages="countryErrors"
          @update:model-value="onChangeCountry"
          @blur="v$.country.$touch()"
        />
        <v-autocomplete
          v-model="selectedLabels"
          :items="labelsItems"
          :multiple="true"
          :loading="isLoadingLabels"
          prepend-inner-icon="label"
          label="Labels"
          item-title="name"
          item-value="id"
          :return-object="true"
          :disabled="!country"
          hint="Select a country first"
          :persistent-hint="!country"
          :error-messages="labelsErrors"
          v-model:search="selectedLabelsSearch"
          @blur="v$.selectedLabels.$touch()"
        />
      </magic-container>

      <div class="component-actions">
        <v-btn color="secondary" @click="onUpdate">
          <v-icon>{{ triggerIcon }}</v-icon>
          {{ triggerLabel }}
        </v-btn>
      </div>
    </div>
    <div class="component-body">
      <Removables
        v-if="hasItems"
        :items="formattedLabels"
        :is-disabled="isDisabled"
        :on-remove="onRemove"
      >
        <template v-slot:item="scope">
          <div class="d-flex align-center">
            <v-icon>public</v-icon>
            <span>{{ scope.item.country.name }}</span>
          </div>
          <div class="labels">
            <span
              v-for="label in scope.item.labels"
              :key="label.id"
              class="label"
            >
              <v-icon>label</v-icon>
              <span>{{ label.name }}</span>
            </span>
          </div>
        </template>
      </Removables>
    </div>
  </div>
</template>

<script>
  import { useVuelidate } from '@vuelidate/core'
  import { required } from '@vuelidate/validators';
  import { v1 as uuidv1 } from 'uuid';
  import Removables from '../../lists/Removables';
  import { isObj, isStr, isArr, debounce } from '../../../utils';

  const debouncer = debounce({
    id: uuidv1(),
    timeout: 500
  });

  export default {
    components: {
      Removables,
    },
    setup () {
    return {
      v$: useVuelidate()
    }
  },
    validations() {
      return {
        selectedLabels: {
          required,
        },
        country: {
          required,
        },
      };
    },
    props: {
      packageItem: {
        type: Object,
        default: undefined,
      },
      labels: {
        type: Array,
        default() {
          return [];
        },
      },
      triggerIcon: {
        type: String,
        default: 'edit',
      },
      triggerLabel: {
        type: String,
        default: 'Attach',
      },
      isDisabled: {
        type: Boolean,
        default: false,
      },
      onSubmit: {
        type: Function,
        default: () => {},
      },
      onRemove: {
        type: Function,
        default: () => {},
      },
    },
    data() {
      return {
        selectedLabelsSearch: '',
        selectedLabels: [],
        isLoadingLabels: false,
        isRefreshingLabels: true,
        country: null,
      };
    },
    computed: {
      countries() {
        return this.$store.getters['users/countries'];
      },
      labelsItems() {
        return this.$store.getters['labels/items'];
      },
      formattedLabels() {
        return this.transformLabels(this.labels);
      },
      hasItems() {
        return this.formattedLabels.length > 0;
      },
      labelsErrors() {
        return this.v$.selectedLabels.$errors.map(error => {
          if (error.$validator === 'required') {
            return 'Labels are required';
          }
          return error.$message;
        });
      },
      countryErrors() {
        return this.v$.country.$errors.map(error => {
          if (error.$validator === 'required') {
            return 'Country is required';
          }
          return error.$message;
        });
      },
    },
    watch: {
      packageItem(nextPackageItem) {
        if (isObj(nextPackageItem)) {
          this.clear();
        }
      },
      selectedLabels() {
        this.isRefreshingLabels = true;
      },
      selectedLabelsSearch(nextSearch) {
        if (isStr(nextSearch)) {
          if (this.isRefreshingLabels) {
            this.isRefreshingLabels = false;
            return false;
          }

          debouncer(() => this.searchLabels(nextSearch));
        }
      },
    },
    methods: {
      onUpdate() {
        this.v$.$touch();

        if (!this.canSubmitRequest() || this.v$.$error || this.v$.pending) {
          return false;
        }

        const { country, selectedLabels, onSubmit } = this;

        onSubmit({
          labels: selectedLabels.map(({ id }) => id),
          country,
        }).then(() => {
          this.clear();
        });
      },
      canSubmitRequest() {
        const { country, selectedLabels } = this;

        return isStr(country.code) && isArr(selectedLabels);
      },
      onChangeCountry(country) {
        this.selectedLabels = [];
        this.isLoadingLabels = true;
        return this.$store.dispatch('labels/getItems', {
          countries: [country.code],
          perPage: -1,
        }).finally(() => {
          this.isLoadingLabels = false;
        });
      },
      searchLabels(search = '') {
        this.isLoadingLabels = true;
        return this.$store.dispatch('labels/getItems', {
          search,
          countries: [this.country.code],
        }).finally(() => {
          this.isLoadingLabels = false;
        });
      },
      transformLabels(labels) {
        let nextLabels = [];

        labels.forEach((label) => {
          const { country } = label;
          const isExistingCountry =
            nextLabels.length > 0 &&
            nextLabels.filter((l) => l.country.code === country.code).length > 0;

          if (isExistingCountry) {
            nextLabels = nextLabels.map((l) => ({
              ...l,
              labels:
                l.country.code === country.code ? [...l.labels, label] : l.labels,
            }));
          } else {
            nextLabels.push({
              country,
              labels: [label],
            });
          }
        });

        return nextLabels;
      },
      clear() {
        this.v$.$reset();
        this.country = null;
        this.selectedLabels = [];
        this.isRefreshingLabels = false;
      },
    },
  };
</script>

<style lang="scss">
@use 'sass:map';
@use '../../../assets/styles/theme' as *;

[data-component-group='package-form'][data-component='package-labels'] {
  padding: $spacing-xl;
  background-color: rgb(var(--v-theme-surface));
  border-top: 1px solid map.get($colors, grey, lighten-2);

  .component-actions {
    text-align: right;
    padding: 8px 0 16px 0;

    .v-btn {
      margin: 0;

      .v-icon {
        margin: 0 6px 0 0;
      }
    }
  }

  .labels {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-wrap: wrap;
  }

  .label {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    font-size: $text-size-sm;
    padding: 2px 6px;
    margin: 0 10px 4px 0;
    border-radius: $border-radius;
    color: map.get($colors, shades, 'white');
    background-color: map.get($colors, primary);

    .v-icon {
      font-size: $text-size-md;
      margin: 0 6px 0 0;
      color: map.get($colors, shades, 'white');
    }
  }

  [data-component='removables'] {
    .v-list {
      padding: 0;
    }

    .v-list-item {
      height: auto;
      padding: 12px 16px;
      border-radius: $border-radius;
      background-color: rgba(var(--v-theme-on-surface), 0.06);
    }
  }
}
</style>
